import { type CSSProperties, type ReactNode } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { LuPanelLeftClose, LuPanelLeftOpen } from 'react-icons/lu';

import { SIDEBAR_WIDTH } from '../../constants/sidebar';
import { useSwipeToClose } from '../../hooks/useSwipeToClose';

const stylesheet = {
  container:
    'md:transition-[width] md:ease-in-out md:duration-300 absolute md:static',
  expanded:
    'absolute md:w-sidebar-outer w-sidebar min-h-screen md:min-h-0 bg-semantic-bg-surface md:bg-transparent',
  mobileExpanded: 'fixed! top-0 bottom-0 left-0 w-sidebar z-sidebar-drawer',
  contractedDesktop:
    'fixed top-0 md:static md:w-sidebar-rail-outer md:bg-transparent',
  contractedMobile: 'fixed top-0 h-[50px] z-sidebar-mobile-bar',
  nav: {
    // md:h-[calc(100%-2rem)], not 100vh: the aside is stretched by its flex
    // parent (Layout's .container, itself flex-1 min-h-0 under the trial
    // banner) so its height already excludes the banner — a vh-based calc
    // would keep assuming the sidebar owns the full viewport and overflow.
    container:
      'h-full md:h-[calc(100%-2rem)] pt-6 md:py-4 flex flex-col md:rounded-semantic md:border md:border-semantic-border transition-[width,padding] duration-300 ease-in-out bg-semantic-bg-surface md:m-4',
    expanded: 'w-full md:w-sidebar px-[14px]',
    contracted: 'w-full md:w-sidebar-rail items-center px-[13px]',
  },
  ul: 'flex flex-col flex-1 overflow-y-auto',
  sidebarOverlay: 'fixed inset-0 bg-semantic-black/50 z-sidebar-overlay',
  overflowWrapper:
    'flex flex-col flex-1 overflow-y-auto scrollbar-hide pb-[30px]',
  overflowWrapperContracted: 'scrollbar-hide',
  navFlexGrow: 'flex flex-col gap-4 flex-1',
  ulContracted: 'items-center',
  footerWrapper: 'flex flex-col pt-4 mt-auto',
  footerWrapperContracted: 'pb-6',
  composeButtonWrapper:
    'flex items-start justify-center self-center h-fit w-full',
  headerSlotWrapper: 'w-full',
  desktopHeader: 'flex items-center mb-[14px]',
  desktopLogoWrapper: 'overflow-hidden h-[36px] flex items-center',
  desktopHeaderExpanded: 'justify-between',
  desktopHeaderContracted: 'justify-center',
  desktopHeaderCollapsedWithLogo: 'flex-col justify-center gap-2',
  desktopCollapsedLogoWrapper: 'flex items-center justify-center',
  desktopToggleButton:
    'w-[31px] h-[31px] rounded-semantic flex items-center justify-center text-semantic-text-secondary bg-transparent border-none cursor-pointer hover:bg-semantic-bg-hover',
  mobileLogoWrapper: 'overflow-hidden h-[36px] flex items-center mb-[14px]',
  mobileSidebarNav:
    'fixed top-0 bottom-0 left-0 w-sidebar bg-semantic-bg-surface z-sidebar-drawer pt-6 px-[14px] flex flex-col overflow-hidden',
};

const MOBILE_NAV_STYLE: CSSProperties = {
  paddingBottom: 'env(safe-area-inset-bottom, 0px)',
  touchAction: 'pan-y',
};

export interface AppSidebarShellProps {
  logoSlot: ReactNode;
  isMobile: boolean;
  shouldShowExpanded: boolean;
  onToggleOpen: () => void;
  headerSlot?: ReactNode;
  composeSlot?: ReactNode;
  bodySlot: ReactNode;
  footerSlot: ReactNode;
  toggleAriaLabel: string;
  collapsedLogoSlot?: ReactNode;
}

interface SidebarShellBodyProps {
  shouldShowExpanded: boolean;
  headerSlot?: ReactNode;
  composeSlot?: ReactNode;
  bodySlot: ReactNode;
  footerSlot: ReactNode;
}

function SidebarShellBody({
  shouldShowExpanded,
  headerSlot,
  composeSlot,
  bodySlot,
  footerSlot,
}: Readonly<SidebarShellBodyProps>) {
  return (
    <>
      {headerSlot !== undefined && (
        <div className={stylesheet.headerSlotWrapper}>{headerSlot}</div>
      )}

      {composeSlot !== undefined && (
        <div className={stylesheet.composeButtonWrapper}>{composeSlot}</div>
      )}

      <div
        className={`${stylesheet.overflowWrapper} ${shouldShowExpanded ? '' : stylesheet.overflowWrapperContracted}`}
      >
        <div className={stylesheet.navFlexGrow}>
          <div
            className={`${stylesheet.ul} ${shouldShowExpanded ? '' : stylesheet.ulContracted}`}
          >
            {bodySlot}
          </div>
        </div>

        <footer
          className={`${stylesheet.footerWrapper} ${shouldShowExpanded ? '' : stylesheet.footerWrapperContracted}`}
        >
          {footerSlot}
        </footer>
      </div>
    </>
  );
}

function buildContainerClass(
  shouldShowExpanded: boolean,
  isMobile: boolean,
): string {
  if (shouldShowExpanded) {
    return isMobile ? stylesheet.mobileExpanded : stylesheet.expanded;
  }
  return isMobile ? stylesheet.contractedMobile : stylesheet.contractedDesktop;
}

export function AppSidebarShell({
  logoSlot,
  isMobile,
  shouldShowExpanded,
  onToggleOpen,
  headerSlot,
  composeSlot,
  bodySlot,
  footerSlot,
  toggleAriaLabel,
  collapsedLogoSlot,
}: Readonly<AppSidebarShellProps>) {
  const { swipeX, onTouchStart, onTouchMove, onTouchEnd } =
    useSwipeToClose(onToggleOpen);

  const showCollapsedLogo =
    !isMobile && !shouldShowExpanded && collapsedLogoSlot !== undefined;

  return (
    <>
      <aside
        className={`${stylesheet.container} ${buildContainerClass(shouldShowExpanded, isMobile)}`}
      >
        {!isMobile && (
          <nav
            data-testid="sidebar-rail"
            className={`${stylesheet.nav.container} ${shouldShowExpanded ? stylesheet.nav.expanded : stylesheet.nav.contracted}`}
          >
            <div
              className={`${stylesheet.desktopHeader} ${shouldShowExpanded ? stylesheet.desktopHeaderExpanded : showCollapsedLogo ? stylesheet.desktopHeaderCollapsedWithLogo : stylesheet.desktopHeaderContracted}`}
            >
              {shouldShowExpanded && (
                <div className={stylesheet.desktopLogoWrapper}>{logoSlot}</div>
              )}
              <button
                type="button"
                className={stylesheet.desktopToggleButton}
                onClick={onToggleOpen}
                aria-label={toggleAriaLabel}
                title={toggleAriaLabel}
              >
                {shouldShowExpanded ? (
                  <LuPanelLeftClose />
                ) : (
                  <LuPanelLeftOpen />
                )}
              </button>
              {showCollapsedLogo && (
                <div className={stylesheet.desktopCollapsedLogoWrapper}>
                  {collapsedLogoSlot}
                </div>
              )}
            </div>

            <SidebarShellBody
              shouldShowExpanded={shouldShowExpanded}
              headerSlot={headerSlot}
              composeSlot={composeSlot}
              bodySlot={bodySlot}
              footerSlot={footerSlot}
            />
          </nav>
        )}
      </aside>

      <AnimatePresence>
        {isMobile && shouldShowExpanded && (
          <motion.nav
            key="mobile-sidebar-nav"
            data-testid="sidebar-mobile-drawer"
            className={stylesheet.mobileSidebarNav}
            style={{ ...MOBILE_NAV_STYLE, x: swipeX }}
            initial={{ x: -SIDEBAR_WIDTH }}
            animate={{ x: 0 }}
            exit={{ x: -SIDEBAR_WIDTH }}
            transition={{
              type: 'spring',
              damping: 35,
              stiffness: 300,
            }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div className={stylesheet.mobileLogoWrapper}>{logoSlot}</div>

            <SidebarShellBody
              shouldShowExpanded={shouldShowExpanded}
              headerSlot={headerSlot}
              composeSlot={composeSlot}
              bodySlot={bodySlot}
              footerSlot={footerSlot}
            />
          </motion.nav>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {shouldShowExpanded && isMobile && (
          <motion.div
            data-testid="sidebar-mobile-overlay"
            className={stylesheet.sidebarOverlay}
            onClick={onToggleOpen}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />
        )}
      </AnimatePresence>
    </>
  );
}
